import React, { useEffect, useState } from 'react';

interface TOCItem {
  id: string;
  text: string;
  level: number;
}

interface TableOfContentsProps {
  contentHtml: string;
}

function extractHeadings(html: string): TOCItem[] {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const headings = doc.querySelectorAll('h2, h3');
  const items: TOCItem[] = [];

  headings.forEach((heading) => {
    const text = heading.textContent?.trim() || '';
    const id = text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
    items.push({ id, text, level: parseInt(heading.tagName[1]) });
  });

  return items;
}

const TableOfContents: React.FC<TableOfContentsProps> = ({ contentHtml }) => {
  const [items, setItems] = useState<TOCItem[]>([]);
  const [activeId, setActiveId] = useState('');

  useEffect(() => {
    setItems(extractHeadings(contentHtml));
  }, [contentHtml]);

  useEffect(() => {
    if (items.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
            break;
          }
        }
      },
      { rootMargin: '-80px 0px -60% 0px', threshold: 0 }
    );

    items.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [items]);

  if (items.length < 3) return null;

  return (
    <nav className="toc">
      <p className="toc__title">Contents</p>
      <ul className="toc__list">
        {items.map(({ id, text, level }) => (
          <li key={id} className="toc__item">
            <a
              href={`#${id}`}
              className={`toc__link${level === 3 ? ' toc__link--h3' : ''}${activeId === id ? ' toc__link--active' : ''}`}
            >
              {text}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
};

export default TableOfContents;
